import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import {
  useFirestoreConnect,
  isLoaded,
  useFirebase,
} from 'react-redux-firebase';
import deepCompare from 'deep-equal';
import moment from 'moment';

import { useTheme } from '../../ThemeContext';

import Helmet from '../../components/Helmet';
import LoadingData from '../../components/LoadingData';
import Header from '../../components/Header';
import Sider from '../../components/Sider';
import Filter from '../../components/Filter';
import { SingleDataAnnotation as SingleAnnotation } from '../../components/SingleAnnotation';
import { MultiDataAnnotation as MultiAnnotation } from '../../components/MultiAnnotation';

import {
  dataAnnotationPhotoDocumentReleaseInit,
  dataAnnotationPhotoDocumentRelease,
  dataAnnotationPhotoDocumentReserveInit,
  dataAnnotationPhotoDocumentReserve,
  dataAnnotationPhotoDocumentSaveInit,
  dataAnnotationPhotoDocumentSave,
  dataAnnotationPhotoDocumentFinishInit,
  dataAnnotationPhotoDocumentFinish,
  dataAnnotationPhotoDocumentsReleaseInit,
  dataAnnotationPhotoDocumentsRelease,
  dataAnnotationPhotoDocumentsReserveInit,
  dataAnnotationPhotoDocumentsReserve,
  dataAnnotationPhotoDocumentsSaveInit,
  dataAnnotationPhotoDocumentsSave,
  dataAnnotationPhotoDocumentsFinishInit,
  dataAnnotationPhotoDocumentsFinish,
  dataAnnotationPhotoDocumentDeleteInit,
  dataAnnotationPhotoDocumentDelete,
} from '../../actions/DataAnnotation/DataAnnotationActions';

import {
  Section,
  Wrapper,
  Content,
  Panel,
  PanelInner,
  Information,
  Text,
  Button,
  Center,
} from './DataAnnotationPage.style';

const DataAnnotationPage = () => {
  const DATA_ANNOTATION_PHOTO_DOCUMENT_LOCALSTORAGE =
    'APP_DATA_ANNOTATION_PHOTO_DOCUMENT';
  const DATA_ANNOTATION_TYPE_LOCALSTORAGE = 'APP_DATA_ANNOTATION_TYPE_CHANGE';
  const DATA_ANNOTATION_TIMESTAMP_LOCALSTORAGE =
    'APP_DATA_ANNOTATION_TIMESTAMP_CHANGE';
  const DATA_ANNOTATION_PHOTO_DOCUMENT_TAGS_CHANGE_LOCALSTORAGE =
    'APP_DATA_ANNOTATION_PHOTO_DOCUMENT_TAGS_CHANGE';
  const DATA_ANNOTATION_PHOTO_DOCUMENT_OBJECTS_CHANGE_LOCALSTORAGE =
    'APP_DATA_ANNOTATION_PHOTO_DOCUMENT_OBJECTS_CHANGE';
  const [defaultMultiAnnotationPhotoDocumentsLimit] = useState(8);
  const intl = useIntl();
  const theme = useTheme();
  const dispatch = useDispatch();
  const firebase = useFirebase();
  const { profile } = useSelector((state) => state.firebase);
  const isProfileCompany = profile.company && profile.company.id ? true : false;
  const filterStore = useSelector((state) => state.filterStore);

  const [annotationType, setAnnotationType] = useState(
    JSON.parse(localStorage.getItem(DATA_ANNOTATION_TYPE_LOCALSTORAGE)) ||
      'SINGLE_LABELING'
  );
  const [annotationTimestamp, setAnnotationTimestamp] = useState(
    JSON.parse(localStorage.getItem(DATA_ANNOTATION_TIMESTAMP_LOCALSTORAGE)) ||
      undefined
  );

  const photoDocuments = useSelector(
    (state) => state.firestore.ordered['photosDataAnnotation']
  );

  const [reservedPhotoDocumentsId, setReservedPhotoDocumentsId] = useState(
    JSON.parse(
      localStorage.getItem(DATA_ANNOTATION_PHOTO_DOCUMENT_LOCALSTORAGE)
    ) || undefined
  );

  const [selectedPhotoDocuments, setSelectedPhotoDocuments] = useState(
    undefined
  );
  const [
    selectedPhotoDocumentSelectedObject,
    setSelectedPhotoDocumentSelectedObject,
  ] = useState(undefined);

  const reservedPhotoDocuments = useSelector(
    (state) =>
      annotationTimestamp &&
      state.firestore.ordered[
        `reservedPhotosDataAnnotation/${annotationTimestamp}`
      ]
  );

  const [previousPhotoDocuments, setPreviousPhotoDocuments] = useState(
    undefined
  );

  const [
    changedPhotoTagsDataAnnotation,
    setChangedPhotoTagsDataAnnotation,
  ] = useState(
    JSON.parse(
      localStorage.getItem(
        DATA_ANNOTATION_PHOTO_DOCUMENT_TAGS_CHANGE_LOCALSTORAGE
      )
    ) || undefined
  );
  const [
    changedObjectsDataAnnotation,
    setChangedObjectsDataAnnotation,
  ] = useState(
    JSON.parse(
      localStorage.getItem(
        DATA_ANNOTATION_PHOTO_DOCUMENT_OBJECTS_CHANGE_LOCALSTORAGE
      )
    ) || undefined
  );

  const {
    isLoading: isLoadingReserve,
    isError: isErrorReserve,
    error: errorReserve,
  } = useSelector((state) => state.dataAnnotationPhotoDocumentReserveStore);

  const { isLoading: isLoadingSave } = useSelector(
    (state) => state.dataAnnotationPhotoDocumentSaveStore
  );

  const { isLoading: isLoadingFinish } = useSelector(
    (state) => state.dataAnnotationPhotoDocumentFinishStore
  );

  const { isLoading: isLoadingRelease } = useSelector(
    (state) => state.dataAnnotationPhotoDocumentReleaseStore
  );

  const { isLoading: isLoadingDelete } = useSelector(
    (state) => state.dataAnnotationPhotoDocumentDeleteStore
  );

  const handleReserveDataAnnotation = (options = {}) => {
    const { id, remainingReservedPhotoDocumentsId } = options;
    if (isProfileCompany) {
      if (
        (!id && filterStore.multilabeling) ||
        (remainingReservedPhotoDocumentsId &&
          remainingReservedPhotoDocumentsId.length > 0)
      ) {
        const countAvailableReservePhotoDocuments = filterStore.multilabeling
          ? defaultMultiAnnotationPhotoDocumentsLimit -
            ((remainingReservedPhotoDocumentsId &&
              remainingReservedPhotoDocumentsId.length) ||
              0)
          : 0;
        const photoDocumentsIds =
          photoDocuments &&
          photoDocuments
            .map((photoDocument) => photoDocument.id)
            .slice(0, countAvailableReservePhotoDocuments);
        if (photoDocumentsIds && photoDocumentsIds.length > 0) {
          dispatch(
            dataAnnotationPhotoDocumentsReserve({
              ids: photoDocumentsIds,
            })
          ).then((res) => {
            if (res) {
              setAnnotationType('MULTI_LABELING');
              if (remainingReservedPhotoDocumentsId) {
                setReservedPhotoDocumentsId([
                  ...remainingReservedPhotoDocumentsId,
                  ...photoDocumentsIds,
                ]);
              } else setReservedPhotoDocumentsId([...photoDocumentsIds]);
              // setSelectedPhotoDocuments(undefined);
              // setChangedPhotoTagsDataAnnotation(undefined);
              // setChangedObjectsDataAnnotation(undefined);
              // setSelectedPhotoDocumentSelectedObject(undefined);
            }
            dispatch(dataAnnotationPhotoDocumentsReserveInit());
          });
        }
      } else {
        const photoDocumentId =
          id || (photoDocuments && photoDocuments[0] && photoDocuments[0].id);
        if (photoDocumentId) {
          dispatch(
            dataAnnotationPhotoDocumentReserve({
              id: photoDocumentId,
            })
          ).then((res) => {
            if (res) {
              setAnnotationType('SINGLE_LABELING');
              setReservedPhotoDocumentsId([photoDocumentId]);
              // setSelectedPhotoDocuments(undefined);
              // setChangedPhotoTagsDataAnnotation(undefined);
              // setChangedObjectsDataAnnotation(undefined);
              // setSelectedPhotoDocumentSelectedObject(undefined);
            }
            dispatch(dataAnnotationPhotoDocumentReserveInit());
          });
        }
      }
    }
  };

  const handleSaveDataAnnotation = (options = {}) => {
    const { id, isRejected = false } = options;
    if (isProfileCompany) {
      if (annotationType === 'MULTI_LABELING') {
        const data = [];
        if (
          id ||
          (selectedPhotoDocuments && selectedPhotoDocuments.length > 0)
        ) {
          if (id) {
            const selectedPhotoDocument = reservedPhotoDocuments.find(
              (reservedPhotoDocument) => reservedPhotoDocument.id === id
            );

            if (selectedPhotoDocument) {
              const photoTagsAutomaticAnnotation =
                (selectedPhotoDocument.automatic_annotation &&
                  selectedPhotoDocument.automatic_annotation.photo &&
                  selectedPhotoDocument.automatic_annotation.photo.tags) ||
                [];
              const photoTagsManualAnnotation =
                (selectedPhotoDocument.vinne_annotation &&
                  selectedPhotoDocument.vinne_annotation.photo &&
                  selectedPhotoDocument.vinne_annotation.photo.tags) ||
                [];

              const currentPhotoTagsManualAnnotation = isRejected
                ? photoTagsManualAnnotation
                : (changedPhotoTagsDataAnnotation &&
                    Object.entries(changedPhotoTagsDataAnnotation).map(
                      ([category, value]) => `${category}.${value}`
                    )) ||
                  [];

              const isChangedPhotoTagsAnnotation = deepCompare(
                photoTagsAutomaticAnnotation,
                currentPhotoTagsManualAnnotation
              )
                ? false
                : true;

              data.push({
                id,
                photoTags: currentPhotoTagsManualAnnotation,
                isChangedPhotoTags: isChangedPhotoTagsAnnotation,
              });
            }
          } else {
            selectedPhotoDocuments.forEach(({ id, ...photoDocument }) => {
              const photoTagsAutomaticAnnotation =
                (photoDocument.automatic_annotation &&
                  photoDocument.automatic_annotation.photo &&
                  photoDocument.automatic_annotation.photo.tags) ||
                [];
              const photoTagsManualAnnotation =
                (photoDocument.vinne_annotation &&
                  photoDocument.vinne_annotation.photo &&
                  photoDocument.vinne_annotation.photo.tags) ||
                [];

              const currentPhotoTagsManualAnnotation = isRejected
                ? photoTagsManualAnnotation
                : (changedPhotoTagsDataAnnotation &&
                    Object.entries(changedPhotoTagsDataAnnotation).map(
                      ([category, value]) => `${category}.${value}`
                    )) ||
                  [];

              const isChangedPhotoTagsAnnotation = deepCompare(
                photoTagsAutomaticAnnotation,
                currentPhotoTagsManualAnnotation
              )
                ? false
                : true;

              data.push({
                id,
                photoTags: currentPhotoTagsManualAnnotation,
                isChangedPhotoTags: isChangedPhotoTagsAnnotation,
              });
            });
          }

          dispatch(
            dataAnnotationPhotoDocumentsSave({
              data,
              isRejected,
            })
          ).then((res) => {
            const remainingReservedPhotoDocumentsId = reservedPhotoDocumentsId.filter(
              (reservedPhotoDocumentId) =>
                !(
                  data &&
                  data.find(
                    (photoDocument) =>
                      photoDocument.id === reservedPhotoDocumentId
                  )
                )
            );
            if (res) {
              if (
                !(
                  remainingReservedPhotoDocumentsId &&
                  remainingReservedPhotoDocumentsId.length > 0
                )
              )
                setAnnotationType(undefined);
              setReservedPhotoDocumentsId(
                remainingReservedPhotoDocumentsId &&
                  remainingReservedPhotoDocumentsId.length > 0
                  ? remainingReservedPhotoDocumentsId
                  : undefined
              );
              setSelectedPhotoDocuments(undefined);
              setChangedPhotoTagsDataAnnotation(undefined);
            }
            dispatch(dataAnnotationPhotoDocumentsSaveInit());

            if (res)
              if (
                photoDocuments &&
                photoDocuments.length > 0 &&
                (!(
                  remainingReservedPhotoDocumentsId &&
                  remainingReservedPhotoDocumentsId.length > 0
                ) ||
                  (remainingReservedPhotoDocumentsId &&
                    remainingReservedPhotoDocumentsId.length > 0 &&
                    reservedPhotoDocuments &&
                    reservedPhotoDocuments.length > 0 &&
                    reservedPhotoDocuments[0].data_source_id ===
                      filterStore.dataSource))
              )
                handleReserveDataAnnotation({
                  remainingReservedPhotoDocumentsId,
                });
          });
        }
      } else {
        if (selectedPhotoDocuments && selectedPhotoDocuments.length > 0) {
          const { id, ...photoDocument } = selectedPhotoDocuments[0];
          const photoTagsAutomaticAnnotation =
            (photoDocument.automatic_annotation &&
              photoDocument.automatic_annotation.photo &&
              photoDocument.automatic_annotation.photo.tags) ||
            [];
          const photoTagsManualAnnotation =
            (photoDocument.vinne_annotation &&
              photoDocument.vinne_annotation.photo &&
              photoDocument.vinne_annotation.photo.tags) ||
            [];

          const currentPhotoTagsManualAnnotation = isRejected
            ? photoTagsManualAnnotation
            : (changedPhotoTagsDataAnnotation &&
                Object.entries(changedPhotoTagsDataAnnotation).map(
                  ([category, value]) => `${category}.${value}`
                )) ||
              [];

          const isChangedPhotoTagsAnnotation = deepCompare(
            photoTagsAutomaticAnnotation,
            currentPhotoTagsManualAnnotation
          )
            ? false
            : true;

          const objectsAutomaticAnnotation =
            (photoDocument.automatic_annotation &&
              photoDocument.automatic_annotation.objects &&
              photoDocument.automatic_annotation.objects.data) ||
            [];
          const objectsManualAnnotation =
            (photoDocument.vinne_annotation &&
              photoDocument.vinne_annotation.objects &&
              photoDocument.vinne_annotation.objects.data) ||
            [];

          const currentObjectsManualAnnotation = isRejected
            ? objectsManualAnnotation
            : (changedObjectsDataAnnotation &&
                changedObjectsDataAnnotation.map((object) => ({
                  ...object,
                  tags:
                    (object.tags &&
                      Object.entries(object.tags).map(
                        ([category, value]) => `${category}.${value}`
                      )) ||
                    [],
                }))) ||
              [];

          const isChangedObjectsAnnotation = deepCompare(
            objectsAutomaticAnnotation,
            currentObjectsManualAnnotation
          )
            ? false
            : true;

          dispatch(
            dataAnnotationPhotoDocumentSave({
              id,
              photoTags: currentPhotoTagsManualAnnotation,
              isChangedPhotoTags: isChangedPhotoTagsAnnotation,
              objects: currentObjectsManualAnnotation,
              isChangedObjects: isChangedObjectsAnnotation,
              isRejected,
            })
          ).then((res) => {
            if (res) {
              if (previousPhotoDocuments)
                setPreviousPhotoDocuments([...previousPhotoDocuments, id]);
              else setPreviousPhotoDocuments([id]);
              setReservedPhotoDocumentsId(undefined);
              setSelectedPhotoDocuments(undefined);
              setSelectedPhotoDocumentSelectedObject(undefined);
              setChangedPhotoTagsDataAnnotation(undefined);
              setChangedObjectsDataAnnotation(undefined);
            }
            dispatch(dataAnnotationPhotoDocumentSaveInit());
            if (photoDocuments && photoDocuments.length > 0)
              handleReserveDataAnnotation();
          });
        }
      }
    }
  };

  const handleFinishDataAnnotation = () => {
    if (isProfileCompany) {
      if (annotationType === 'MULTI_LABELING') {
        const remainingReservedPhotoDocumentsId = reservedPhotoDocumentsId.filter(
          (id) =>
            !(
              selectedPhotoDocuments &&
              selectedPhotoDocuments.find(
                (selectedPhotoDocument) => selectedPhotoDocument.id === id
              )
            )
        );
        if (selectedPhotoDocuments && selectedPhotoDocuments.length > 0) {
          const data = [];
          selectedPhotoDocuments.forEach(({ id, ...photoDocument }) => {
            const photoTagsAutomaticAnnotation =
              (photoDocument.automatic_annotation &&
                photoDocument.automatic_annotation.photo &&
                photoDocument.automatic_annotation.photo.tags) ||
              [];
            const currentPhotoTagsManualAnnotation =
              (changedPhotoTagsDataAnnotation &&
                Object.entries(changedPhotoTagsDataAnnotation).map(
                  ([category, value]) => `${category}.${value}`
                )) ||
              [];
            const isChangedPhotoTagsAnnotation = deepCompare(
              photoTagsAutomaticAnnotation,
              currentPhotoTagsManualAnnotation
            )
              ? false
              : true;

            data.push({
              id,
              photoTags: currentPhotoTagsManualAnnotation,
              isChangedPhotoTags: isChangedPhotoTagsAnnotation,
            });
          });

          dispatch(
            dataAnnotationPhotoDocumentsFinish({
              data,
            })
          ).then((res) => {
            if (res) {
              dispatch(
                dataAnnotationPhotoDocumentsRelease({
                  ids: remainingReservedPhotoDocumentsId,
                })
              ).then((res) => {
                if (res) {
                  setAnnotationType(undefined);
                  setReservedPhotoDocumentsId(undefined);
                  setSelectedPhotoDocuments(undefined);
                  setChangedPhotoTagsDataAnnotation(undefined);
                }
                dispatch(dataAnnotationPhotoDocumentsReleaseInit());
              });
            }
            dispatch(dataAnnotationPhotoDocumentsFinishInit());
          });
        } else {
          dispatch(
            dataAnnotationPhotoDocumentsRelease({
              ids: remainingReservedPhotoDocumentsId,
            })
          ).then((res) => {
            if (res) {
              setAnnotationType(undefined);
              setReservedPhotoDocumentsId(undefined);
              setSelectedPhotoDocuments(undefined);
              setChangedPhotoTagsDataAnnotation(undefined);
            }
            dispatch(dataAnnotationPhotoDocumentsReleaseInit());
          });
        }
      } else {
        if (selectedPhotoDocuments && selectedPhotoDocuments.length > 0) {
          const { id, ...photoDocument } = selectedPhotoDocuments[0];
          const photoTagsAutomaticAnnotation =
            (photoDocument.automatic_annotation &&
              photoDocument.automatic_annotation.photo &&
              photoDocument.automatic_annotation.photo.tags) ||
            [];
          const currentPhotoTagsManualAnnotation =
            (changedPhotoTagsDataAnnotation &&
              Object.entries(changedPhotoTagsDataAnnotation).map(
                ([category, value]) => `${category}.${value}`
              )) ||
            [];
          const isChangedPhotoTagsAnnotation = deepCompare(
            photoTagsAutomaticAnnotation,
            currentPhotoTagsManualAnnotation
          )
            ? false
            : true;

          const objectsAutomaticAnnotation =
            (photoDocument.automatic_annotation &&
              photoDocument.automatic_annotation.objects &&
              photoDocument.automatic_annotation.objects.tags) ||
            [];
          const currentObjectsManualAnnotation =
            (changedObjectsDataAnnotation &&
              changedObjectsDataAnnotation.map((object) => ({
                ...object,
                tags:
                  (object.tags &&
                    Object.entries(object.tags).map(
                      ([category, value]) => `${category}.${value}`
                    )) ||
                  [],
              }))) ||
            [];
          const isChangedObjectsAnnotation = deepCompare(
            objectsAutomaticAnnotation,
            currentObjectsManualAnnotation
          )
            ? false
            : true;

          dispatch(
            dataAnnotationPhotoDocumentFinish({
              id,
              photoTags: currentPhotoTagsManualAnnotation,
              isChangedPhotoTags: isChangedPhotoTagsAnnotation,
              objects: currentObjectsManualAnnotation,
              isChangedObjects: isChangedObjectsAnnotation,
            })
          ).then((res) => {
            if (res) {
              setPreviousPhotoDocuments(undefined);
              setReservedPhotoDocumentsId(undefined);
              setSelectedPhotoDocuments(undefined);
              setSelectedPhotoDocumentSelectedObject(undefined);
              setChangedPhotoTagsDataAnnotation(undefined);
              setChangedObjectsDataAnnotation(undefined);
            }
            dispatch(dataAnnotationPhotoDocumentFinishInit());
          });
        }
      }
    }
  };

  const handleDeletePhotoDocument = (options = {}) => {
    const { id } = options;
    if (isProfileCompany) {
      if (annotationType === 'MULTI_LABELING') {
        if (id) {
          dispatch(
            dataAnnotationPhotoDocumentDelete({
              id,
            })
          ).then((res) => {
            const remainingReservedPhotoDocumentsId = reservedPhotoDocumentsId.filter(
              (reservedPhotoDocumentId) => reservedPhotoDocumentId !== id
            );
            if (res) {
              setReservedPhotoDocumentsId(
                remainingReservedPhotoDocumentsId &&
                  remainingReservedPhotoDocumentsId.length > 0
                  ? remainingReservedPhotoDocumentsId
                  : undefined
              );
              setSelectedPhotoDocuments(undefined);
              setChangedPhotoTagsDataAnnotation(undefined);
            }
            dispatch(dataAnnotationPhotoDocumentDeleteInit());
            if (res)
              if (
                photoDocuments &&
                photoDocuments.length > 0 &&
                (!(
                  remainingReservedPhotoDocumentsId &&
                  remainingReservedPhotoDocumentsId.length > 0
                ) ||
                  (remainingReservedPhotoDocumentsId &&
                    remainingReservedPhotoDocumentsId.length > 0 &&
                    reservedPhotoDocuments &&
                    reservedPhotoDocuments.length > 0 &&
                    reservedPhotoDocuments[0].data_source_id ===
                      filterStore.dataSource))
              )
                handleReserveDataAnnotation({
                  remainingReservedPhotoDocumentsId,
                });
          });
        }
      } else {
        if (selectedPhotoDocuments && selectedPhotoDocuments.length > 0) {
          const { id } = selectedPhotoDocuments[0];
          dispatch(
            dataAnnotationPhotoDocumentDelete({
              id,
            })
          ).then((res) => {
            if (res) {
              setReservedPhotoDocumentsId(undefined);
              setSelectedPhotoDocuments(undefined);
              setSelectedPhotoDocumentSelectedObject(undefined);
              setChangedPhotoTagsDataAnnotation(undefined);
              setChangedObjectsDataAnnotation(undefined);
            }
            dispatch(dataAnnotationPhotoDocumentDeleteInit());
            if (res) {
              if (photoDocuments && photoDocuments.length > 0)
                handleReserveDataAnnotation();
            }
          });
        }
      }
    }
  };

  const handleChangePreviousDataAnnotation = () => {
    if (isProfileCompany) {
      if (annotationType === 'MULTI_LABELING') {
        throw new Error(
          'NOT_IMPLEMENTED_CHANGE_PREVIOUS_PHOTO_DOCUMENT_MULTI_LABELING'
        );
      } else {
        if (selectedPhotoDocuments && selectedPhotoDocuments.length > 0) {
          const { id } = selectedPhotoDocuments[0];
          dispatch(
            dataAnnotationPhotoDocumentRelease({
              id,
            })
          ).then((res) => {
            if (res) {
              setReservedPhotoDocumentsId(undefined);
              setSelectedPhotoDocuments(undefined);
              setSelectedPhotoDocumentSelectedObject(undefined);
              setChangedPhotoTagsDataAnnotation(undefined);
              setChangedObjectsDataAnnotation(undefined);
            }
            dispatch(dataAnnotationPhotoDocumentReleaseInit());
            if (res) {
              if (previousPhotoDocuments && previousPhotoDocuments.length > 0) {
                const previousPhotoDocument = previousPhotoDocuments.pop();
                setPreviousPhotoDocuments(previousPhotoDocuments);
                handleReserveDataAnnotation({ id: previousPhotoDocument });
              }
            }
          });
        }
      }
    }
  };

  const handleReleaseDataAnnotation = () => {
    if (isProfileCompany) {
      const reservedPhotoDocumentsId = JSON.parse(
        localStorage.getItem(DATA_ANNOTATION_PHOTO_DOCUMENT_LOCALSTORAGE)
      );
      const annotationType = JSON.parse(
        localStorage.getItem(DATA_ANNOTATION_TYPE_LOCALSTORAGE)
      );
      if (reservedPhotoDocumentsId && reservedPhotoDocumentsId.length > 0) {
        if (annotationType === 'MULTI_LABELING') {
          handleClearReservedPhotoDocumentDataAnnotationLocalStorage();
          handleClearTypeDataAnnotationLocalStorage();
          handleClearTimestampDataAnnotationLocalStorage();
          handleClearPhotoTagsDataAnnotationLocalStorage();
          handleClearObjectsDataAnnotationLocalStorage();
          dispatch(
            dataAnnotationPhotoDocumentsRelease({
              ids: reservedPhotoDocumentsId,
            })
          ).then(() => {
            dispatch(dataAnnotationPhotoDocumentsReleaseInit());
          });
        } else {
          handleClearReservedPhotoDocumentDataAnnotationLocalStorage();
          handleClearTypeDataAnnotationLocalStorage();
          handleClearTimestampDataAnnotationLocalStorage();
          handleClearPhotoTagsDataAnnotationLocalStorage();
          handleClearObjectsDataAnnotationLocalStorage();
          dispatch(
            dataAnnotationPhotoDocumentRelease({
              id: reservedPhotoDocumentsId[0],
            })
          ).then(() => {
            dispatch(dataAnnotationPhotoDocumentReleaseInit());
          });
        }
      }
    }
  };

  const handleClearReservedPhotoDocumentDataAnnotationLocalStorage = () => {
    localStorage.removeItem(DATA_ANNOTATION_PHOTO_DOCUMENT_LOCALSTORAGE);
  };

  const handleClearTypeDataAnnotationLocalStorage = () => {
    localStorage.removeItem(DATA_ANNOTATION_TYPE_LOCALSTORAGE);
  };

  const handleClearTimestampDataAnnotationLocalStorage = () => {
    localStorage.removeItem(DATA_ANNOTATION_TIMESTAMP_LOCALSTORAGE);
  };

  const handleClearPhotoTagsDataAnnotationLocalStorage = () => {
    localStorage.removeItem(
      DATA_ANNOTATION_PHOTO_DOCUMENT_TAGS_CHANGE_LOCALSTORAGE
    );
  };

  const handleClearObjectsDataAnnotationLocalStorage = () => {
    localStorage.removeItem(
      DATA_ANNOTATION_PHOTO_DOCUMENT_OBJECTS_CHANGE_LOCALSTORAGE
    );
  };

  const handleFilterAnnotation = (type, data) => {
    if (type === 'PHOTO') {
      if (changedPhotoTagsDataAnnotation)
        setChangedPhotoTagsDataAnnotation({
          ...changedPhotoTagsDataAnnotation,
          ...data,
        });
      else
        setChangedPhotoTagsDataAnnotation({
          ...data,
        });
    } else if (type === 'OBJECT') {
      if (changedObjectsDataAnnotation) {
        setChangedObjectsDataAnnotation([
          ...changedObjectsDataAnnotation,
          ...data.filter(
            (object) =>
              !changedObjectsDataAnnotation.find(
                (arrayObject) => arrayObject.id === object.id
              )
          ),
        ]);
      } else setChangedObjectsDataAnnotation([...data]);
    }
  };

  const handleChangeDataAnnotation = (
    type,
    { categoryId, tag, startX, startY, endX, endY }
  ) => {
    if (type === 'PHOTO') {
      if (categoryId && tag) {
        if (changedPhotoTagsDataAnnotation) {
          if (changedPhotoTagsDataAnnotation[categoryId] === tag) {
            const newChangedPhotoCategoriesDataAnnotation = {
              ...changedPhotoTagsDataAnnotation,
            };
            delete newChangedPhotoCategoriesDataAnnotation[categoryId];

            setChangedPhotoTagsDataAnnotation({
              ...newChangedPhotoCategoriesDataAnnotation,
            });
          } else
            setChangedPhotoTagsDataAnnotation({
              ...changedPhotoTagsDataAnnotation,
              [categoryId]: tag,
            });
        } else
          setChangedPhotoTagsDataAnnotation({
            [categoryId]: tag,
          });
      }
    } else if (type === 'OBJECT') {
      if (selectedPhotoDocumentSelectedObject !== undefined) {
        const object = changedObjectsDataAnnotation.find(
          (object) => object.id === selectedPhotoDocumentSelectedObject
        );
        if (object) {
          const newObject = Object.assign({}, object);
          const newObjectTags = Object.assign({}, newObject.tags);

          if (newObjectTags[categoryId] && newObjectTags[categoryId] === tag)
            delete newObjectTags[categoryId];
          else newObjectTags[categoryId] = tag;

          setChangedObjectsDataAnnotation([
            ...changedObjectsDataAnnotation.map((object) =>
              object.id === newObject.id
                ? { ...newObject, tags: newObjectTags }
                : object
            ),
          ]);
        }
      }
    } else if (type === 'OBJECT_CHANGE') {
      if (selectedPhotoDocumentSelectedObject !== undefined) {
        const object = changedObjectsDataAnnotation.find(
          (object) => object.id === selectedPhotoDocumentSelectedObject
        );
        if (object) {
          const newObject = Object.assign({}, object);
          newObject.coordinates = [
            { x: startX, y: startY },
            { x: endX, y: endY },
          ];
          setChangedObjectsDataAnnotation([
            ...changedObjectsDataAnnotation.map((object) =>
              object.id === newObject.id ? newObject : object
            ),
          ]);
        }
      }
    } else if (type === 'OBJECT_CREATE') {
      if (selectedPhotoDocumentSelectedObject === undefined) {
        setChangedObjectsDataAnnotation([
          ...changedObjectsDataAnnotation,
          {
            id: changedObjectsDataAnnotation.length,
            coordinates: [
              { x: startX, y: startY },
              { x: endX, y: endY },
            ],
            tags: [],
          },
        ]);
      }
    } else if (type === 'OBJECT_DELETE') {
      if (selectedPhotoDocumentSelectedObject !== undefined) {
        setChangedObjectsDataAnnotation([
          ...changedObjectsDataAnnotation
            .filter(
              (object) => object.id !== selectedPhotoDocumentSelectedObject
            )
            .map((object, index) => ({ ...object, id: index })),
        ]);
        setSelectedPhotoDocumentSelectedObject(undefined);
      }
    }
  };

  const firestoreConnect = [];
  if (isProfileCompany) {
    const dataSourceTags =
      filterStore.dataSource !== undefined && filterStore.tags !== undefined
        ? (() => {
            const tags = Object.entries(filterStore.tags).reduce(
              (currentState, [category, tags]) => {
                if (tags) {
                  return [
                    ...currentState,
                    ...tags.split(',').map((tag) => `${category}.${tag}`),
                  ];
                }
                return currentState;
              },
              []
            );

            if (tags && tags.length > 0)
              return [
                `${filterStore.recognition || 'vinne_annotation'}.photo.tags`,
                'array-contains-any',
                tags.slice(0, 10),
              ];
            return undefined;
          })()
        : undefined;

    firestoreConnect.push({
      collection: 'companies',
      doc: profile.company.id,
      subcollections: [
        {
          collection: 'photos',
          orderBy: [['creation_date', 'asc']],
          where: [
            ['vinne_annotation.annotated', '==', false],
            ['reserved', '==', false],
            ['rejected', '==', false],
            filterStore.origin !== undefined && [
              'origin_id',
              '==',
              filterStore.origin,
            ],
            filterStore.startDate !== undefined && [
              'creation_date',
              '>=',
              filterStore.startDate,
            ],
            filterStore.endDate !== undefined && [
              'creation_date',
              '<=',
              filterStore.endDate,
            ],
            filterStore.dataSource !== undefined && [
              'data_source_id',
              '==',
              filterStore.dataSource,
            ],
            dataSourceTags,
          ].filter((criteria) => criteria),
          limit: filterStore.multilabeling
            ? defaultMultiAnnotationPhotoDocumentsLimit
            : 1,
        },
      ],
      storeAs: `photosDataAnnotation`,
    });
    if (
      annotationTimestamp &&
      reservedPhotoDocumentsId &&
      reservedPhotoDocumentsId.length > 0
    ) {
      firestoreConnect.push({
        collection: 'companies',
        doc: profile.company.id,
        subcollections: [
          {
            collection: 'photos',
            where: [
              firebase.firestore.FieldPath.documentId(),
              'in',
              reservedPhotoDocumentsId.slice(0, 10),
            ],
          },
        ],
        storeAs: `reservedPhotosDataAnnotation/${annotationTimestamp}`,
      });
    }
  }
  useFirestoreConnect(firestoreConnect);

  useEffect(() => {
    return () => handleReleaseDataAnnotation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (reservedPhotoDocumentsId)
      localStorage.setItem(
        DATA_ANNOTATION_PHOTO_DOCUMENT_LOCALSTORAGE,
        JSON.stringify(reservedPhotoDocumentsId)
      );
    else handleClearReservedPhotoDocumentDataAnnotationLocalStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservedPhotoDocumentsId]);

  useEffect(() => {
    if (annotationType)
      localStorage.setItem(
        DATA_ANNOTATION_TYPE_LOCALSTORAGE,
        JSON.stringify(annotationType)
      );
    else handleClearTypeDataAnnotationLocalStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annotationType]);

  useEffect(() => {
    if (annotationTimestamp)
      localStorage.setItem(
        DATA_ANNOTATION_TIMESTAMP_LOCALSTORAGE,
        JSON.stringify(annotationTimestamp)
      );
    else handleClearTimestampDataAnnotationLocalStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annotationTimestamp]);

  useEffect(() => {
    if (changedPhotoTagsDataAnnotation)
      localStorage.setItem(
        DATA_ANNOTATION_PHOTO_DOCUMENT_TAGS_CHANGE_LOCALSTORAGE,
        JSON.stringify(changedPhotoTagsDataAnnotation)
      );
    else handleClearPhotoTagsDataAnnotationLocalStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changedPhotoTagsDataAnnotation]);

  useEffect(() => {
    if (changedObjectsDataAnnotation)
      localStorage.setItem(
        DATA_ANNOTATION_PHOTO_DOCUMENT_OBJECTS_CHANGE_LOCALSTORAGE,
        JSON.stringify(changedObjectsDataAnnotation)
      );
    else handleClearObjectsDataAnnotationLocalStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changedObjectsDataAnnotation]);

  useEffect(() => {
    if (
      isLoaded(reservedPhotoDocuments) &&
      reservedPhotoDocuments &&
      selectedPhotoDocuments
    ) {
      setSelectedPhotoDocuments(
        selectedPhotoDocuments.filter((selectedPhotoDocument) =>
          reservedPhotoDocuments.find(
            (reservedPhotoDocument) =>
              reservedPhotoDocument.id === selectedPhotoDocument.id &&
              !reservedPhotoDocument.rejected
          )
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservedPhotoDocuments]);

  useEffect(() => {
    if (
      isLoaded(reservedPhotoDocuments) &&
      reservedPhotoDocuments &&
      reservedPhotoDocumentsId
    ) {
      setReservedPhotoDocumentsId(
        reservedPhotoDocumentsId.filter((id) =>
          reservedPhotoDocuments.find(
            (reservedPhotoDocument) =>
              reservedPhotoDocument.id === id && !reservedPhotoDocument.rejected
          )
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservedPhotoDocuments]);

  const renderInformation = (information, children) => {
    return (
      <Center>
        <Information theme={theme.content.information.default}>
          <Text>{information}</Text>
          {children ? children : ''}
        </Information>
      </Center>
    );
  };

  const renderSingleAnnotation = () => {
    const currentAnnotations = reservedPhotoDocuments.filter((photoDocument) =>
      reservedPhotoDocumentsId.includes(photoDocument.id)
    );
    if (currentAnnotations && currentAnnotations.length > 0) {
      const currentAnnotation =
        selectedPhotoDocuments && selectedPhotoDocuments.length > 0
          ? currentAnnotations.find(
              (photoDocument) =>
                String(photoDocument.id) ===
                String(selectedPhotoDocuments[0].id)
            )
          : currentAnnotations[0];
      if (currentAnnotation) {
        return (
          <SingleAnnotation
            data={currentAnnotation}
            handleDeletePhotoDocument={() => handleDeletePhotoDocument()}
            handleSaveAnnotation={(options) =>
              handleSaveDataAnnotation(options)
            }
            handleFinishAnnotation={() => handleFinishDataAnnotation()}
            handleFilterAnnotation={(type, tags) =>
              handleFilterAnnotation(type, tags)
            }
            changedPhotoTagsAnnotation={changedPhotoTagsDataAnnotation}
            handleChangeAnnotation={(type, data) =>
              handleChangeDataAnnotation(type, data)
            }
            changedObjectsAnnotation={changedObjectsDataAnnotation}
            handleSelectPhotoDocuments={(data) =>
              setSelectedPhotoDocuments(data)
            }
            selectedObject={selectedPhotoDocumentSelectedObject}
            handleSelectObject={(id) =>
              setSelectedPhotoDocumentSelectedObject(id)
            }
            isLoading={
              isLoadingReserve ||
              isLoadingSave ||
              isLoadingFinish ||
              isLoadingRelease ||
              isLoadingDelete
            }
            previousAnnotation={previousPhotoDocuments}
            handleChangePhotoDocument={() =>
              handleChangePreviousDataAnnotation()
            }
          />
        );
      }
    }
    return <LoadingData fullmode={true} />;
  };

  const renderMultipleAnnotation = () => {
    const currentAnnotations = reservedPhotoDocuments.filter((photoDocument) =>
      reservedPhotoDocumentsId.includes(photoDocument.id)
    );
    if (currentAnnotations && currentAnnotations.length > 0) {
      return (
        <MultiAnnotation
          data={currentAnnotations}
          handleDeletePhotoDocument={(options) =>
            handleDeletePhotoDocument(options)
          }
          handleSaveAnnotation={(options) => handleSaveDataAnnotation(options)}
          handleFinishAnnotation={() => handleFinishDataAnnotation()}
          changedPhotoTagsAnnotation={changedPhotoTagsDataAnnotation}
          handleChangeAnnotation={(type, data) =>
            handleChangeDataAnnotation(type, data)
          }
          selectedPhotoDocuments={selectedPhotoDocuments}
          handleSelectPhotoDocuments={(data) => setSelectedPhotoDocuments(data)}
          isLoading={
            isLoadingReserve ||
            isLoadingSave ||
            isLoadingFinish ||
            isLoadingRelease
          }
        />
      );
    }
    return <LoadingData fullmode={true} />;
  };

  return (
    <>
      <Helmet
        title={intl.formatMessage({ id: 'PAGE_TITLE.DATA_ANNOTATION' })}
      />
      <Section>
        <Sider />
        <Wrapper>
          <Header
            title={intl.formatMessage({ id: 'PAGE_TITLE.DATA_ANNOTATION' })}
          />
          <Content>
            <Panel theme={theme.content.panel.default}>
              <PanelInner>
                {!isProfileCompany ? (
                  renderInformation(
                    intl.formatMessage({
                      id: `DATA_ANNOTATION.YOU_HAVE_NO_ASSIGNED_COMPANY`,
                    })
                  )
                ) : isLoaded(photoDocuments) ? (
                  reservedPhotoDocumentsId &&
                  reservedPhotoDocumentsId.length > 0 &&
                  isLoaded(reservedPhotoDocuments) &&
                  reservedPhotoDocuments &&
                  reservedPhotoDocuments.length > 0 ? (
                    annotationType === 'SINGLE_LABELING' ? (
                      renderSingleAnnotation()
                    ) : annotationType === 'MULTI_LABELING' ? (
                      renderMultipleAnnotation()
                    ) : (
                      ''
                    )
                  ) : (reservedPhotoDocumentsId &&
                      reservedPhotoDocumentsId.length > 0 &&
                      !isLoaded(reservedPhotoDocuments)) ||
                    isLoadingReserve ||
                    isLoadingSave ||
                    isLoadingFinish ||
                    isLoadingRelease ||
                    isLoadingDelete ? (
                    <LoadingData fullmode={true} />
                  ) : !(photoDocuments && photoDocuments.length > 0) ? (
                    renderInformation(
                      intl.formatMessage({
                        id: `DATA_ANNOTATION.YOU_HAVE_NO_PHOTO_DOCUMENTS`,
                      })
                    )
                  ) : (
                    renderInformation(
                      isErrorReserve
                        ? errorReserve
                        : intl.formatMessage({
                            id: `DATA_ANNOTATION.YOU_CAN_EXPAND_MENU_WITH_FILTERS_ON_THE_RIGHT_OR_START_LABELING_BY_CLICKING_THE_START_BUTTON`,
                          }),
                      <Button
                        theme={theme.button.default}
                        onClick={() => {
                          setAnnotationTimestamp(moment().unix());
                          handleReserveDataAnnotation();
                        }}
                        disabled={isLoadingReserve}
                      >
                        {isLoadingReserve
                          ? intl.formatMessage({
                              id: `DATA_ANNOTATION.PLEASE_WAIT`,
                            })
                          : intl.formatMessage({
                              id: `DATA_ANNOTATION.START`,
                            })}
                      </Button>
                    )
                  )
                ) : (
                  <LoadingData fullmode={true} />
                )}
              </PanelInner>
            </Panel>
          </Content>
        </Wrapper>
        {isProfileCompany && <Filter availableFilterMultilabeling={true} />}
      </Section>
    </>
  );
};

export default DataAnnotationPage;
